import React, { createContext, useContext, useState, ReactNode } from 'react';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { format, subDays, subMonths, subWeeks, startOfDay } from 'date-fns';

export type DatePreset =
  | 'last7days'
  | 'last30days'
  | 'last3months'
  | 'last6months'
  | 'last12months'
  | 'custom';

export const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: 'last7days', label: 'Last 7 days' },
  { value: 'last30days', label: 'Last 30 days' },
  { value: 'last3months', label: 'Last 3 months' },
  { value: 'last6months', label: 'Last 6 months' },
  { value: 'last12months', label: 'Last 12 months' },
  { value: 'custom', label: 'Custom range' },
];

export function getPresetLabel(preset: DatePreset): string {
  return DATE_PRESETS.find(p => p.value === preset)?.label ?? 'Custom range';
}

// Returns yyyy-MM-dd strings for API consumption
function computePresetDates(
  preset: DatePreset,
): { from: string; to: string } | null {
  const today = new Date();
  const toDate = format(today, 'yyyy-MM-dd');
  switch (preset) {
    case 'last7days':
      return {
        from: format(startOfDay(subDays(today, 6)), 'yyyy-MM-dd'),
        to: toDate,
      };
    case 'last30days':
      return {
        from: format(startOfDay(subDays(today, 29)), 'yyyy-MM-dd'),
        to: toDate,
      };
    case 'last3months':
      return {
        from: format(startOfDay(subMonths(today, 3)), 'yyyy-MM-dd'),
        to: toDate,
      };
    case 'last6months':
      return {
        from: format(startOfDay(subMonths(today, 6)), 'yyyy-MM-dd'),
        to: toDate,
      };
    case 'last12months':
      return {
        from: format(startOfDay(subWeeks(today, 52)), 'yyyy-MM-dd'),
        to: toDate,
      };
    default:
      return null;
  }
}

const DEFAULT_PRESET: DatePreset = 'last7days';
const DEFAULT_DATES = computePresetDates(DEFAULT_PRESET)!;

interface AnalyticsContextType {
  selectedInstallationId: string;
  setSelectedInstallationId: (id: string) => void;
  // yyyy-MM-dd strings consumed by API queries
  dateFrom: string;
  dateTo: string;
  selectedPreset: DatePreset;
  applyPreset: (
    preset: DatePreset,
    customFrom?: string,
    customTo?: string,
  ) => void;
  selectedCallingAet: string | null;
  setSelectedCallingAet: (callingAet: string | null) => void;
  isInternalUser: boolean;
  isDemoUser: boolean;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(
  undefined,
);

export const useAnalyticsContext = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error(
      'useAnalyticsContext must be used within AnalyticsProvider',
    );
  }
  return context;
};

interface AnalyticsProviderProps {
  children: ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
}) => {
  const identityApi = useApi(identityApiRef);
  const [isInternalUser, setIsInternalUser] = useState(false);
  const [isDemoUser, setIsDemoUser] = useState(false);
  const [selectedInstallationId, setSelectedInstallationId] = useState<string>(
    () => localStorage.getItem('analytics_selectedInstallationId') || '',
  );

  const [selectedPreset, setSelectedPreset] = useState<DatePreset>(() => {
    const stored = localStorage.getItem('analytics_selectedPreset');
    return DATE_PRESETS.some(p => p.value === stored)
      ? (stored as DatePreset)
      : DEFAULT_PRESET;
  });

  const [customFrom, setCustomFrom] = useState<string>(
    () => localStorage.getItem('analytics_dateFrom') || DEFAULT_DATES.from,
  );
  const [customTo, setCustomTo] = useState<string>(
    () => localStorage.getItem('analytics_dateTo') || DEFAULT_DATES.to,
  );

  const { dateFrom, dateTo } =
    selectedPreset === 'custom'
      ? { dateFrom: customFrom, dateTo: customTo }
      : (() => {
          const dates = computePresetDates(selectedPreset) ?? DEFAULT_DATES;
          return { dateFrom: dates.from, dateTo: dates.to };
        })();

  const [selectedCallingAet, setSelectedCallingAet] = useState<string | null>(
    localStorage.getItem('analytics_selectedCallingAet'),
  );

  const applyPreset = (
    preset: DatePreset,
    providedFrom?: string,
    providedTo?: string,
  ) => {
    setSelectedPreset(preset);
    localStorage.setItem('analytics_selectedPreset', preset);

    if (preset !== 'custom') {
      localStorage.removeItem('analytics_dateFrom');
      localStorage.removeItem('analytics_dateTo');
    } else if (providedFrom && providedTo) {
      const from = providedFrom.split('T')[0];
      const to = providedTo.split('T')[0];
      setCustomFrom(from);
      setCustomTo(to);
      localStorage.setItem('analytics_dateFrom', from);
      localStorage.setItem('analytics_dateTo', to);
    }
  };

  React.useEffect(() => {
    identityApi.getProfileInfo().then(profile => {
      setIsInternalUser(profile.email?.endsWith('@deepc.ai') ?? false);
      setIsDemoUser(profile.email === 'deepc_demo@yopmail.com');
    });
  }, [identityApi]);

  React.useEffect(() => {
    localStorage.setItem(
      'analytics_selectedInstallationId',
      selectedInstallationId,
    );
  }, [selectedInstallationId]);

  React.useEffect(() => {
    if (!selectedCallingAet) {
      localStorage.removeItem('analytics_selectedCallingAet');
      return;
    }

    localStorage.setItem('analytics_selectedCallingAet', selectedCallingAet);
  }, [selectedCallingAet]);

  return (
    <AnalyticsContext.Provider
      value={{
        selectedInstallationId,
        setSelectedInstallationId,
        dateFrom,
        dateTo,
        selectedPreset,
        applyPreset,
        selectedCallingAet,
        setSelectedCallingAet,
        isInternalUser,
        isDemoUser,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};
